import { LocationModel } from 'common/data/location/model';
import { LocationTypeEnum } from 'common/data/location/type.enum';
import { PropertyModel } from 'common/data/property/model';
import { PropertyCategoryIdentifierAdapter } from 'common/data/property-category/identifier/adapter';
import { areaMetricToImperial } from 'common/helper/area/metric-to-imperial';
import { configGetCountry } from 'common/helper/config/get-country';
import { filterParametersGetLabelForValue } from 'common/module/filter/parameters/get-label-for-value';
import { FilterParamsEnum } from 'common/module/filter/params.enum';
import { StatsTealiumAdapterAgentSearchInputsInterface } from 'common/module/stats/tealium/adapter/agent-search-inputs.interface';
import { StatsTealiumAdapterAgentSearchParamsInterface } from 'common/module/stats/tealium/adapter/agent-search-params.interface';
import { StatsTealiumAdapterPropertySearchParamsInterface } from 'common/module/stats/tealium/adapter/property-search-params.interface';
import { StatsTealiumDataLayerAgentSearchInterface } from 'common/module/stats/tealium/data-layer/agent-search.interface';
import { StatsTealiumDataLayerPropertyInterface } from 'common/module/stats/tealium/data-layer/property.interface';
import { StatsTealiumDataLayerPropertySearchInterface } from 'common/module/stats/tealium/data-layer/property-search.interface';
import { StatsTealiumAdapterPropertySubcategoriesMapping } from 'common/module/stats/tealium/property-subcategories.mapping';

export class StatsTealiumAdapter {
  /**
   * Transforms agent search form into tealium search model
   * @param params StatsTealiumAdapterAgentSearchParamsInterface
   * @param inputs StatsTealiumAdapterAgentSearchInputsInterface
   * @returns StatsTealiumDataLayerAgentSearchInterface
   */
  public toAgentSearchTealiumStats(
    params: StatsTealiumAdapterAgentSearchParamsInterface,
    inputs: StatsTealiumAdapterAgentSearchInputsInterface
  ): StatsTealiumDataLayerAgentSearchInterface {
    return {
      search_agent_service:
        inputs.category?.find((category) => category.value === params['filter[category_id]'])?.text || '',

      search_agent_nationality:
        inputs.nationality?.find((nationality) => nationality.value === params['filter[nationality]'])?.text || '',

      search_agent_languages:
        inputs.language
          ?.filter((language) => params['filter[language_ids]'].some((filterLang) => filterLang === language.value))
          .map((language) => language.text) || [],

      search_agent_keywords: params['filter[name]'] || '',
    };
  }

  /**
   * Transforms property search form into tealium search model
   * @param params StatsTealiumAdapterPropertySearchParamsInterface
   * @returns StatsTealiumDataLayerPropertySearchInterface
   */
  public toPropertySearchTealiumStats(
    params: StatsTealiumAdapterPropertySearchParamsInterface,
    locations: LocationModel[],
    searchResultTotalCount?: number
  ): StatsTealiumDataLayerPropertySearchInterface {
    const tealiumVars: Pick<
      StatsTealiumDataLayerPropertySearchInterface,
      'search_category' | 'search_furnishing' | 'search_locations' | 'search_rental_period' | 'search_property_type'
    > = {
      search_category: new PropertyCategoryIdentifierAdapter().getData(
        parseInt(params[FilterParamsEnum.categoryId]?.value, 10)
      ),
      search_furnishing: filterParametersGetLabelForValue(params[FilterParamsEnum.furnishing]),
      search_rental_period: filterParametersGetLabelForValue(params[FilterParamsEnum.pricePeriod]),
      search_property_type: filterParametersGetLabelForValue(params[FilterParamsEnum.propertyTypeId]),
      search_locations: locations?.length ? locations.map((location) => location.name) : [],
    };

    return {
      search_amenities: params[FilterParamsEnum.amenities]?.value || [],
      search_completion: params[FilterParamsEnum.completionStatus]?.value || '',
      search_keywords: params[FilterParamsEnum.keyword]?.value || '',
      search_bedrooms: params[FilterParamsEnum.bedrooms]?.value || [],
      search_bathrooms: params[FilterParamsEnum.bathrooms]?.value || [],
      search_max_area: params[FilterParamsEnum.maxArea]?.value || '',
      search_min_area: params[FilterParamsEnum.minArea]?.value || '',
      search_max_price: params[FilterParamsEnum.maxPrice]?.value || '',
      search_min_price: params[FilterParamsEnum.minPrice]?.value || '',
      search_viewings_type: params[FilterParamsEnum.virtualViewings]?.value || '',
      search_verified: params[FilterParamsEnum.verified]?.value || '',
      search_result_count: searchResultTotalCount?.toString() || '',
      search_category_id: params[FilterParamsEnum.categoryId]?.value || '',
      search_property_type_id: params[FilterParamsEnum.propertyTypeId]?.value || '',
      search_location_ids: params[FilterParamsEnum.locationsIds]?.value || [],
      ...tealiumVars,
    };
  }

  public getLocationTreeTypes(): LocationTypeEnum[] {
    switch (configGetCountry().code) {
      case 'ae':
      case 'qa':
        return [
          LocationTypeEnum.city,
          LocationTypeEnum.community,
          LocationTypeEnum.subcommunity,
          LocationTypeEnum.tower,
        ];
      case 'eg':
        return [
          LocationTypeEnum.city,
          LocationTypeEnum.tower,
          LocationTypeEnum.town,
          LocationTypeEnum.subdistrict,
          LocationTypeEnum.district,
          LocationTypeEnum.street,
          LocationTypeEnum.compound,
        ];
      case 'sa':
        return [LocationTypeEnum.city, LocationTypeEnum.district, LocationTypeEnum.region];
      case 'bh':
        return [LocationTypeEnum.community, LocationTypeEnum.region, LocationTypeEnum.area];
    }
  }

  /**
   * Transforms PropertyModel[] into tealium property model
   * @param properties PropertyModel[]
   */
  public toPropertyTealiumStats(properties: PropertyModel[]): StatsTealiumDataLayerPropertyInterface {
    const statsMap: Map<keyof StatsTealiumDataLayerPropertyInterface, string[]> = new Map();

    properties.forEach((property) => {
      statsMap.set('properties_agent_id', [...(statsMap.get('properties_agent_id') || []), property?.agent?.id]);
      statsMap.set('properties_broker_id', [...(statsMap.get('properties_broker_id') || []), property?.broker?.id]);

      const categoryIdentifier = property?.category_identifier;

      statsMap.set('properties_category', [...(statsMap.get('properties_category') || []), categoryIdentifier]);
      statsMap.set('properties_sub_category', [
        ...(statsMap.get('properties_sub_category') || []),
        StatsTealiumAdapterPropertySubcategoriesMapping[categoryIdentifier],
      ]);
      statsMap.set('properties_listing_id', [...(statsMap.get('properties_listing_id') || []), property?.id]);
      statsMap.set('properties_listing_depth', [
        ...(statsMap.get('properties_listing_depth') || []),
        property?.listing_level,
      ]);

      const locationTreeCompact = property.location_tree;

      // https://pfinder.atlassian.net/browse/CX-3646
      [
        LocationTypeEnum.city,
        LocationTypeEnum.community,
        LocationTypeEnum.subcommunity,
        LocationTypeEnum.tower,
      ].forEach((locationType) => {
        const location = locationTreeCompact?.find(
          (location) => location.location_type?.toLocaleLowerCase() === locationType.toLowerCase()
        );

        statsMap.set(
          `properties_location_${locationType.toLowerCase()}` as keyof StatsTealiumDataLayerPropertyInterface,
          [
            ...(statsMap.get(
              `properties_location_${locationType.toLowerCase()}` as keyof StatsTealiumDataLayerPropertyInterface
            ) || []),
            location?.name || '',
          ]
        );
      });

      this.getLocationTreeTypes()?.forEach((type) => {
        const location = locationTreeCompact?.find(
          (location) => location.location_type?.toLocaleLowerCase() === type.toLowerCase()
        );

        statsMap.set(`properties_location_${type.toLowerCase()}_id` as keyof StatsTealiumDataLayerPropertyInterface, [
          ...(statsMap.get(
            `properties_location_${type.toLowerCase()}_id` as keyof StatsTealiumDataLayerPropertyInterface
          ) || []),
          location?.id || '',
        ]);
      });

      statsMap.set('properties_name', [...(statsMap.get('properties_name') || []), property?.name]);
      statsMap.set('properties_price', [...(statsMap.get('properties_price') || []), String(property?.default_price)]);
      statsMap.set('properties_reference', [...(statsMap.get('properties_reference') || []), property?.reference]);
      statsMap.set('properties_rental_period', [
        ...(statsMap.get('properties_rental_period') || []),
        property?.price_period_identifier,
      ]);
      statsMap.set('properties_type', [...(statsMap.get('properties_type') || []), property.property_type?.name]);

      const size = property?.size;
      statsMap.set('properties_size_sqft', [
        ...(statsMap.get('properties_size_sqft') || []),
        String(configGetCountry().areaUnit === 'sqm' ? areaMetricToImperial(size) : size),
      ]);

      statsMap.set('properties_community_topspot', [
        ...(statsMap.get('properties_community_topspot') || []),
        property.cts?.toString(),
      ]);

      statsMap.set('projects_id', [...(statsMap.get('projects_id') || []), property?.project?.id]);
    });

    return Object.fromEntries(statsMap) as unknown as StatsTealiumDataLayerPropertyInterface;
  }
}
